/**
 * EstadoMonitoreoBanner — avisa qué servicios NO se están midiendo.
 *
 * Lee `monitoreo_fuentes` (lo escribe el backend; lo ven admin y supervisor).
 * Una fuente con estado distinto de «ok» significa que el consumo de ese
 * servicio no se está registrando, así que cualquier cifra en cero de esa
 * pantalla no es un dato sino una ausencia. Por eso el aviso va arriba y bien
 * visible, no escondido en un detalle.
 *
 * Si la lectura falla (permisos, red), no muestra nada: nunca rompe la página.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useCollection } from '../../hooks/useFirestore';
import {
  ESTADO_FUENTE_LABELS, aFecha, estadoEfectivo,
  type EstadoFuente, type FuenteDoc,
} from './costosUtil';
import styles from './EstadoMonitoreoBanner.module.css';

interface Props {
  /** Versión reducida (sin requisito) para el monitor de alertas. */
  compacto?: boolean;
}

const hace = (d: Date | null) => {
  if (!d) return 'nunca';
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 24 ? `hace ${h} h` : `hace ${Math.round(h / 24)} d`;
};

interface Fila { fuente: FuenteDoc; estado: EstadoFuente }

export default function EstadoMonitoreoBanner({ compacto = false }: Props) {
  const { data, error } = useCollection<FuenteDoc>('monitoreo_fuentes');
  // «Desactualizado» depende del reloj: se reevalúa cada minuto aunque no llegue un cambio.
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const { problemas, total } = useMemo(() => {
    const filas: Fila[] = (data ?? []).map((f) => ({ fuente: f, estado: estadoEfectivo(f, ahora) }));
    const malas = filas
      .filter((f) => f.estado !== 'ok')
      .sort((a, b) =>
        Number(!!b.fuente.obligatoria) - Number(!!a.fuente.obligatoria)
        || (a.fuente.nombre ?? a.fuente.id).localeCompare(b.fuente.nombre ?? b.fuente.id));
    return { problemas: malas, total: filas.length };
  }, [data, ahora]);

  if (error) {
    console.warn('[EstadoMonitoreoBanner] no se pudo leer monitoreo_fuentes', error);
    return null;
  }
  if (total === 0) return null;

  if (problemas.length === 0) {
    return (
      <p className={styles.todoBien} role="status">
        <CheckCircle2 size={14} aria-hidden="true" /> Todos los servicios se están midiendo
      </p>
    );
  }

  const hayObligatoria = problemas.some((p) => p.fuente.obligatoria);
  const n = problemas.length;
  const titulo = n === 1 ? '1 servicio no se está midiendo' : `${n} servicios no se están midiendo`;
  const Icono = hayObligatoria ? AlertOctagon : AlertTriangle;

  return (
    <section
      className={`${styles.banner} ${hayObligatoria ? styles.rojo : styles.ambar} ${compacto ? styles.compacto : ''}`}
      aria-label="Estado del monitoreo de servicios"
    >
      <div className={styles.cabecera}>
        <Icono size={compacto ? 16 : 20} aria-hidden="true" />
        <div>
          <h2 className={styles.titulo}>{titulo}</h2>
          {!compacto && (
            <p className={styles.sub}>
              Mientras no se resuelvan, el consumo y el costo de estos servicios <strong>no son
              completos</strong>: un valor en cero puede ser simplemente un dato que falta.
            </p>
          )}
        </div>
      </div>

      <ul className={styles.lista}>
        {problemas.map(({ fuente, estado }) => (
          <li
            key={fuente.id}
            className={`${styles.item} ${fuente.obligatoria ? styles.itemRojo : styles.itemAmbar}`}
          >
            <div className={styles.itemHead}>
              <span className={styles.nombre}>{fuente.nombre ?? fuente.id}</span>
              <span className={styles.estado}>{ESTADO_FUENTE_LABELS[estado]}</span>
              <span className={styles.nivel}>{fuente.obligatoria ? 'Obligatoria' : 'Opcional'}</span>
            </div>
            {fuente.mensaje && <p className={styles.mensaje}>{fuente.mensaje}</p>}
            {!compacto && fuente.requisito && (
              <p className={styles.requisito}><strong>Qué hacer:</strong> {fuente.requisito}</p>
            )}
            {!compacto && (
              <p className={styles.meta}>Último dato correcto: {hace(aFecha(fuente.ultimoExito))}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
